// Utility functions for DealerDashboard (filters, sorting, CSV, transforms, stats)

const parseCurrencyToNumber = (value) => {
  if (value == null) return 0;
  const n = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const parseSignedDays = (val) => {
  if (!val) return 0;
  const m = val.toString().match(/-?\d+/);
  return m ? parseInt(m[0], 10) : 0;
};

const parseDateToTs = (value) => {
  if (!value) return 0;
  const d = new Date(value);
  const ts = d.getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const formatDateHuman = (value) => {
  if (!value) return '';
  const d = new Date(value);
  const ts = d.getTime();
  if (!Number.isFinite(ts)) return String(value);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const normalizeString = (v) => (v == null ? '' : String(v).toLowerCase());

const inPriceBucket = (amount, buckets) => {
  if (!buckets || buckets.length === 0) return true;
  const a = parseCurrencyToNumber(amount);
  return buckets.some((b) => {
    switch (b) {
      case 'under-20k': return a < 20000;
      case '20k-30k': return a >= 20000 && a < 30000;
      case '30k-40k': return a >= 30000 && a < 40000;
      case '40k-50k': return a >= 40000 && a < 50000;
      case 'over-50k': return a >= 50000;
      default: return true;
    }
  });
};

const inDateBucket = (dateStr, buckets) => {
  if (!buckets || buckets.length === 0) return true;
  const d = new Date(dateStr);
  if (!Number.isFinite(d.getTime())) return false;

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfThisWeek = new Date(startOfToday);
  startOfThisWeek.setDate(startOfThisWeek.getDate() - startOfThisWeek.getDay()); // Sunday
  const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);

  return buckets.some((b) => {
    switch (b) {
      case 'today':
        return d >= startOfToday;
      case 'this-week':
        return d >= startOfThisWeek;
      case 'this-month':
        return d >= startOfThisMonth;
      case 'last-month':
        return d >= startOfLastMonth && d <= endOfLastMonth;
      default:
        return true;
    }
  });
};

const shallowClone = (arr) => Array.isArray(arr) ? arr.slice() : [];

export const applyFiltersAndSort = (data, filters = {}, sort = {}, isLeftSwipes = true) => {
  let rows = shallowClone(data);

  // Apply filters
  if (filters && Object.keys(filters).length > 0) {
    rows = rows.filter((row) => {
      // For each filter key, the row must satisfy at least one selected value
      return Object.entries(filters).every(([key, selected]) => {
        if (!selected || selected.length === 0) return true;

        // Price/MSRP buckets
        if ((isLeftSwipes && key === 'price') || (!isLeftSwipes && (key === 'msrp'))) {
          const priceVal = isLeftSwipes ? row.price : row.msrp;
          return inPriceBucket(priceVal, selected);
        }

        // Date buckets
        if ((isLeftSwipes && key === 'rejectedDate') || (!isLeftSwipes && key === 'loanExpiry')) {
          const dateVal = isLeftSwipes ? row.rejectedDate : row.loanExpiry;
          return inDateBucket(dateVal, selected);
        }

        // Generic equality match (string-insensitive)
        const cell = row[key];
        if (cell == null) return false;
        const cellNorm = normalizeString(cell);
        return selected.some((sv) => cellNorm === normalizeString(sv));
      });
    });
  }

  // Apply sorting (first non-null key wins)
  const sortKey = Object.keys(sort).find((k) => sort[k] === 'asc' || sort[k] === 'desc');
  if (sortKey) {
    const direction = sort[sortKey] === 'desc' ? -1 : 1;
    rows.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      let aCmp;
      let bCmp;
      if (sortKey === 'price' || sortKey === 'msrp') {
        aCmp = parseCurrencyToNumber(aVal);
        bCmp = parseCurrencyToNumber(bVal);
      } else if (sortKey === 'rejectedDate' || sortKey === 'loanExpiry') {
        aCmp = parseDateToTs(aVal);
        bCmp = parseDateToTs(bVal);
      } else if (sortKey === 'daysRemaining') {
        aCmp = parseSignedDays(aVal);
        bCmp = parseSignedDays(bVal);
      } else {
        aCmp = normalizeString(aVal);
        bCmp = normalizeString(bVal);
      }

      if (aCmp < bCmp) return -1 * direction;
      if (aCmp > bCmp) return 1 * direction;
      return 0;
    });
  }

  return rows;
};

export const convertToCSV = (rows) => {
  const data = Array.isArray(rows) ? rows : [];
  if (data.length === 0) return '';
  const headers = Object.keys(data[0]);
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [headers.join(',')];
  data.forEach((row) => {
    lines.push(headers.map((h) => esc(row[h])).join(','));
  });
  return lines.join('\n');
};

export const downloadCSV = (csvContent, filename = 'export.csv') => {
  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (e) {
    // no-op in non-browser environments
  }
};

// Transformers – keep permissive to avoid crashes if API shapes differ
export const transformToRightSwipes = (garages = [], activeDealerPros = []) => {
  if (!Array.isArray(garages)) return [];

  const pick = (obj, keys) => {
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (obj[k] != null && obj[k] !== '') return obj[k];
    }
    return undefined;
  };

    const normalizeName = (value) => {
        if (value == null) return '';
        const str = String(value);
        return str.replace(/\s+/g, ' ').trim();
    };

  const defaultSalesRep = Array.isArray(activeDealerPros) && activeDealerPros.length > 0
    ? String(activeDealerPros[0])
    : '—';

  return garages.map((g, idx) => {
    const vehicle = pick(g, ['vehicle', 'model', 'vehicleName', 'VehicleName']);
    const loanId = pick(g, ['loanId', 'loan_id', 'LoanId']);
    const vin = pick(g, ['vin', 'VIN', 'VehicleVIN']);
    const msrp = pick(g, ['msrp', 'price', 'MSRP']);
    const stock = pick(g, ['stock', 'stockNo', 'stockNoAlt', 'Stock', 'VehicleStock', 'StockNumber', 'StockNum', 'StockNo']);
    const currentStage = pick(g, ['currentStage', 'stage', 'CurrentStage']);
    const fastPassDealerId = pick(g, ['fastPassDealerId', 'FastPassDealerId', 'fpDealerId', 'FPDealerId']);
    const fastpassVehicleId = pick(g, ['fastpassVehicleId', 'FastpassVehicleId', 'FastPassVehicleId', 'fastpass_vehicle_id']);
    const vehiclePhoto = pick(g, ['vehiclePhoto', 'VehiclePhoto', 'photo', 'Photo', 'photo_url', 'PhotoUrl']);

    // compute loan expiry derived values
    const rawExpiry = pick(g, [
      'loanExpiry','expiry','loanExpire','expiryDate','expireDate','LoanExpiry',
      'LoanExpire','LoanExpireDate','LoanExpiryDate','CloseDate'
    ]);
    let loanExpiryDisplay = rawExpiry ? formatDateHuman(rawExpiry) : '';
    let daysRemainingDisplay = '';
    let statusVal = pick(g, ['status', 'Status']) || 'active';
    if (rawExpiry) {
      const today = new Date();
      const exp = new Date(rawExpiry);
      if (Number.isFinite(exp.getTime())) {
        const diffDaysSigned = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
        daysRemainingDisplay = `${diffDaysSigned} days remaining`;
        if (!pick(g, ['status', 'Status'])) {
          if (diffDaysSigned < 0) statusVal = 'expired';
          else if (diffDaysSigned <= 30) statusVal = 'expiring';
          else statusVal = 'active';
        }
      }
    }

    // Build stage history from individual date fields when provided
    const prospectWaitingDate = pick(g, ['prospectWaitingDate', 'ProspectWaitingDate']);
    const otdProvidedDate = pick(g, ['otdProvidedDate', 'OTDProvidedDate']);
    const leadWaitingDate = pick(g, ['leadWaitingDate', 'LeadWaitingDate']);
    const tdScheduledDate = pick(g, ['tdScheduledDate', 'TDScheduledDate']);
    const fpProvidedDate = pick(g, ['fpProvidedDate', 'FPProvidedDate']);

    const computedStageHistory = [];
    if (prospectWaitingDate) {
      computedStageHistory.push({ stage: 'Prospect Waiting', date: formatDateHuman(prospectWaitingDate), completed: true });
    }
    if (otdProvidedDate) {
      computedStageHistory.push({ stage: 'OTD$ Provided', date: formatDateHuman(otdProvidedDate), completed: true });
    }
    if (leadWaitingDate) {
      computedStageHistory.push({ stage: 'Lead Waiting', date: formatDateHuman(leadWaitingDate), completed: true });
    }
    if (tdScheduledDate) {
      computedStageHistory.push({ stage: 'TD Scheduled', date: formatDateHuman(tdScheduledDate), completed: true });
    }
    if (fpProvidedDate) {
      computedStageHistory.push({ stage: 'FP Provided', date: formatDateHuman(fpProvidedDate), completed: true });
    }

    // Determine current stage if API did not send it explicitly
    let derivedCurrentStage = currentStage;
    if (!derivedCurrentStage) {
      if (fpProvidedDate) derivedCurrentStage = 'FP Provided';
      else if (tdScheduledDate) derivedCurrentStage = 'TD Scheduled';
      else if (leadWaitingDate) derivedCurrentStage = 'Lead Waiting';
      else if (otdProvidedDate) derivedCurrentStage = 'OTD$ Provided';
      else if (prospectWaitingDate) derivedCurrentStage = 'Prospect Waiting';
    }

        const repliedDealerPros = normalizeName(pick(g, ['repliedDealerPros', 'RepliedDealerPros', 'dealerHostName', 'DealerHostName']));
        const derivedSalesRep = normalizeName(pick(g, ['salesRep', 'owner', 'rep', 'SalesRep']));

        return {
      key: String(idx + 1),
            salesRep: repliedDealerPros || derivedSalesRep || defaultSalesRep,
            repliedDealerPros,
      memberDetails: pick(g, [
        'memberDetails','firstName','memberFirstName','MemberFirstName',
        'customerFirstName','CustomerFirstName','Member','MemberName','CustomerName','memberName'
      ]) || '—',
      memberLastName: pick(g, [
        'memberLastName','lastName','memberLastName','MemberLastName',
        'customerLastName','CustomerLastName','LastName'
      ]) || '',
      loanId: loanId || '—',
      fastPassDealerId: fastPassDealerId || '',
      fastpassVehicleId: fastpassVehicleId || '',
      loanExpiry: loanExpiryDisplay,
      daysRemaining: daysRemainingDisplay || pick(g, ['daysRemaining', 'days', 'DaysRemaining']) || '',
      status: statusVal,
      vehicle: vehicle || '—',
      trim: pick(g, ['trim', 'subtrim', 'Trim']) || '',
      vin: vin || '',
      stock: stock || '',
      msrp: msrp || '',
      vehiclePhoto: vehiclePhoto || '',
      currentStage: derivedCurrentStage || 'Prospect Waiting',
      stageHistory: Array.isArray(g.stageHistory) && g.stageHistory.length > 0 ? g.stageHistory : computedStageHistory,
      otdAmount: pick(g, ['otdAmount', 'OTDAmount']) || '',
      otdDate: pick(g, ['otdDate', 'OTDDate']) || '',
      tdRequestDate: pick(g, ['tdRequestDate', 'TDRequestDate']) || '',
      tdScheduledDate: pick(g, ['tdScheduledDate', 'TDScheduledDate']) || '',
      fpProvidedDate: pick(g, ['fpProvidedDate', 'FPProvidedDate']) || ''
    };
  });
};

export const transformToLeftSwipes = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items.map((it, idx) => {
    const pick = (obj, keys) => {
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (obj[k] != null && obj[k] !== '') return obj[k];
      }
      return undefined;
    };

    const year = pick(it, ['year','vehicle_year','VehicleYear']);
    const make = pick(it, ['vehicle_make','make','Make']);
    const model = pick(it, ['vehicle_model','model','Model','vehicle']);
    const composedVehicle = [year, make, model].filter(Boolean).join(' ') || pick(it, ['vehicle','vehicleName']) || '—';

    const milesRaw = pick(it, ['miles','mileage','miles_text']);
    const milesNum = (() => {
      if (milesRaw == null) return undefined;
      const n = Number(String(milesRaw).replace(/[^0-9.-]/g, ''));
      return Number.isFinite(n) ? Math.max(0, Math.round(n)) : undefined;
    })();
    const milesDisplay = milesNum != null ? `${milesNum.toLocaleString()} miles` : (typeof milesRaw === 'string' && milesRaw.trim() ? milesRaw : '');
    const condition = pick(it, ['condition','vehicleCondition']);
    const priceRaw = pick(it, ['price','msrp','price_amount','vehicle_price']);
    const priceDisplay = (() => {
      if (priceRaw == null || priceRaw === '') return '';
      // Keep already currency-formatted strings
      if (/[$]/.test(String(priceRaw))) return String(priceRaw);
      const n = Number(String(priceRaw).replace(/[^0-9.-]/g, ''));
      if (!Number.isFinite(n)) return String(priceRaw);
      return `$${n.toLocaleString()}`;
    })();
    const rejectedDateRaw = pick(it, ['rejectedDate','date','rejected_date','activity_date']);
    const rejectedDateDisplay = rejectedDateRaw ? formatDateHuman(rejectedDateRaw) : '';

    return {
      key: String(idx + 1),
      vin: pick(it, ['vin','VIN']) || '—',
      stockNo: pick(it, ['stockNo','stock','stock_no','StockNumber']) || '—',
      vehicle: composedVehicle,
      subDetail: pick(it, ['subDetail','trim','vehicle_trim']) || '',
      condition: condition || '',
      miles: milesDisplay || '',
      color: pick(it, ['color','extColor','exterior_color','ext_color']) || '',
      price: priceDisplay,
      rejectedDate: rejectedDateDisplay
    };
  });
};

export const getTop5Vehicles = (items = []) => {
  const counts = new Map();
  (items || []).forEach((it) => {
    const key = it.vehicle || it.model || it.vehicleName || it.VehicleName || 'Unknown';
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([vehicle, count]) => ({ vehicle, count }));
};

export const getTestDriveStats = (items = []) => {
  const stats = { prospectWaiting: 0, otdProvided: 0, leadWaiting: 0, tdScheduled: 0, fpProvided: 0 };
  (items || []).forEach((it) => {
    const s = it.currentStage;
    if (s === 'Prospect Waiting') stats.prospectWaiting += 1;
    else if (s === 'OTD$ Provided') stats.otdProvided += 1;
    else if (s === 'Lead Waiting') stats.leadWaiting += 1;
    else if (s === 'TD Scheduled') stats.tdScheduled += 1;
    else if (s === 'FP Provided') stats.fpProvided += 1;
  });
  return stats;
};

const dealerDashboardUtils = {
  applyFiltersAndSort,
  convertToCSV,
  downloadCSV,
  transformToRightSwipes,
  transformToLeftSwipes,
  getTop5Vehicles,
  getTestDriveStats,
};

export default dealerDashboardUtils;


